import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { switchMap, map, mergeMap, take, finalize } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { of, debounceTime } from 'rxjs';
import {
  PlAuthService,
  selectLoadedCurrentUser,
  PLAuthError,
  PLAuthSuccess,
} from 'pl-core-services';
import { environment } from '@environments/environment';
import { PendoLogger } from '@common/pendo.service';
import { HeapLogger } from '@common/heap.service';
import { AuthSyncService } from '@app/auth/auth-sync.service';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { AccountsService } from './user-management/account-users-service';

declare const gtag: Function;
const ALLOWED_GTM_PAGES = [
  '/registration/signup/individual/freemium',
  '/registration/signup-success',
];

@UntilDestroy()
@Component({
  selector: 'pl-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private urlcleaningregex = /\?.*/;

  private authCheckInProgress = false;

  private monitoringInterval: any;

  private log(message: string, data?: any) {
    if (environment.debug) {
      // eslint-disable-next-line no-console
      console.log(`[AppComponent] ${message}`, data || '');
    }
  }

  constructor(
    private store: Store,
    private oidcSecurityService: OidcSecurityService,
    private router: Router,
    private heapLogger: HeapLogger,
    private pendoLogger: PendoLogger,
    private accountsService: AccountsService,
    private authService: PlAuthService,
    private authSyncService: AuthSyncService
  ) {}

  ngOnInit(): void {
    const isSiriusEnabled = getIsSiriusEnabled();
    if (isSiriusEnabled) {
      // const config = this.oidcSecurityService.getConfiguration();

      // NOTE: If you need to, this is a place where you can
      // invoke `this.oidcSecurityService.getAccessToken()`
      // to get initial token state (e.g., inspecting expiry, etc.)
      //
      // It's also a place where you log `{ type: EventTypes[notification.type],
      // value: notification.value }` to see what events are being fired
      // by subscribing to `this.eventService.registerForEvents()` and piping
      // the results after filtering to their `EventTypes` enum values.

      // Subscribe to auth state changes
      this.oidcSecurityService.isAuthenticated$
        .pipe(
          debounceTime(100), // Chrome issues two events in quick succession, Safari does not...giving buffer
          untilDestroyed(this)
        )
        .subscribe((authState: any) => {
          // Don't do anything if we're on the callback route
          if (window.location.pathname === '/auth-callback') {
            // NOTE: Here, we return early to let the guard component handle the callback
            return;
          }
          if (authState?.isAuthenticated) {
            this.oidcSecurityService
              .getUserData()
              .pipe(take(1), untilDestroyed(this))
              .subscribe({
                next: (userData) => {
                  if (userData) {
                    this.handleAuthenticated({
                      isAuthenticated: true,
                      userData,
                    });

                    this.oidcSecurityService
                      .getAccessToken()
                      .pipe(take(1), untilDestroyed(this))
                      .subscribe((token) => {
                        if (token) {
                          this.log('Starting initial token monitoring');
                          // NOTE: Below, you'll see I'm monitoring token state actively,
                          // this is especially important because `angular-auth-oidc-client` at v13.1.0
                          // (and beyond) unfortunately DOES NOT have a robust production-ready
                          // working implementation of silent renew.
                          //
                          // At version v13.1.0 their solution for automatic token refresh
                          // is not only knowingly broken (a fix was posted a few versions later)
                          // - but even once they did post a fix, it has issues (including
                          // users claiming critical problems in their most up-to-date versions).
                          // But more worrisome is that this leader in the Angular auth space
                          // omits critical safety features you would expect from a clientside
                          // auth library.
                          //
                          // So I wrote a thin wrapper around their core library (which, as
                          // you can see is minimally configured and only a few dozen lines of
                          // code itself).
                          //
                          // My code extending `angular-auth-oidc-client` goes well beyond
                          // what any available FE client library offers out of the box - I've
                          // added the following production-grade features to ensure our system
                          // is as robust and secure as possible:
                          //
                          // - Cross-tab authentication synchronization using BroadcastChannel API and leader election
                          // - Web Worker-based token monitoring and refresh scheduling with exponential backoff
                          // - Robust error handling including server downtime scenarios and token refresh failures
                          // - Secure state preservation and restoration mechanisms
                          // - Performance optimizations via worker offloading and state debouncing
                          // - Comprehensive logging and debugging infrastructure
                          // - Graceful degradation during service interruptions
                          // - Deep NgRx store integration
                          // - Session continuity management across multiple tabs
                          // - Intelligent retry mechanisms that distinguish between different failure types
                          //
                          // The code I've written around `angular-auth-oidc-client` is intended to
                          // provide functionality not available in the core library (or others like it):
                          //
                          // - Enabling SECURE + seamless single sign-on across browser tabs without
                          //   re-authentication, WITHOUT using localStorage (e.g. Okta FE SDKs use this
                          //   insecure method to accomplish what I'm doing here with SessionStorage +
                          //   BroadcastChannel + Web Workers)
                          // - Maintaining active sessions during server outages or connectivity issues by
                          //   implementing a robust token refresh mechanism that can handle a variety of
                          //   network failure conditions and server-side issues
                          // - Providing consistent authentication state across all tabs, including
                          //   synchronized token refresh (even if tabs are hidden or backgrounded), allowing
                          //   users who are authenticated and have not explicitly closed their tab or the browser
                          //   to avoid unnecessary re-authentication
                          this.authSyncService.startTokenMonitoring(token);
                        } else {
                          this.log('No token found, skipping token monitoring');
                        }
                      });
                  } else {
                    this.log('User data is null despite being authenticated');
                    this.oidcSecurityService.checkAuth();
                  }
                },
                error: (error) => {
                  this.log('Error fetching user data', error);
                },
              });
          } else if (!this.authCheckInProgress) {
            if (this.authSyncService.isRefreshing) {
              this.log('Skipping auth check - refresh in progress');
              return;
            }
            // NOTE: Not authenticated, if we're not in the middle of
            // an existing/running authentication check, start one...
            this.performAuthCheck();
          }
        });

      // Handle navigation events
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const cleanedUrl = event.url.replace(this.urlcleaningregex, '');

          if (ALLOWED_GTM_PAGES.includes(cleanedUrl))
            gtag('config', 'GTM-KH7KQBC', {
              page_path: event.urlAfterRedirects,
            });
        }
      });
    } else {
      this.store
        .pipe(
          untilDestroyed(this),
          selectLoadedCurrentUser,
          switchMap((currentUser) =>
            this.accountsService.fetchAccounts().pipe(
              mergeMap((accounts: any) => {
                const userAccount = accounts && accounts.length && accounts[0];
                const kangaUser$ = userAccount?.uuid
                  ? this.accountsService.fetchAccountKangaUser(
                      userAccount.uuid,
                      currentUser.uuid
                    )
                  : of(null);
                return kangaUser$.pipe(
                  map((kangaUser: any) => ({
                    currentUser,
                    accounts,
                    kangaUser,
                  }))
                );
              })
            )
          )
        )
        .subscribe(({ currentUser, accounts, kangaUser }) => {
          this.heapLogger.setUser(currentUser);
          this.pendoLogger.setUser(currentUser, accounts, kangaUser);
        });
    }
  }

  ngOnDestroy() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }
  }

  private async performAuthCheck(): Promise<void> {
    if (this.authCheckInProgress) {
      // NOTE: This is a defensive check to ensure we don't
      // trigger race conditions by starting multiple auth checks
      // concurrently.
      return;
    }

    if (window.location.pathname === '/auth-callback') {
      // NOTE: If we're on the callback route, we don't want to
      // duplicate the auth check it's already doing, so we'll skip
      // and early return to prevent incongruent state conditions.
      return;
    }

    this.authCheckInProgress = true;

    const authStateReceived = await this.authSyncService.waitForAuthState();

    if (!authStateReceived) {
      this.log(
        'No auth state received from other tabs, proceeding with fresh auth check'
      );
    }

    this.oidcSecurityService
      .checkAuth()
      .pipe(
        take(1),
        finalize(() => {
          this.authCheckInProgress = false;
        })
      )
      .subscribe({
        next: (authResult) => {
          if (!authResult.isAuthenticated) {
            // NOTE: If we're not authenticated here, we should
            // start the authorization process:
            //
            // This start by invoking `/authorize` on the auth server
            // and will redirect the user to the login page defined
            // on the OIDC client configuration.
            //
            // This will result in subsequent expected calls standard
            // to the OAuth OIDC flow, including the auth server
            // redirecting back to the `auth-callback` route.
            // This is followed by the OIDC client processing the
            // callback during which the auth server's routes for
            // keys, tokens, etc. will be called.
            this.oidcSecurityService.authorize();
          }
        },
        error: (error) => {
          this.store.dispatch(PLAuthError({ error }));
        },
      });
  }

  private handleAuthenticated(authResult: {
    isAuthenticated: boolean;
    userData: any;
  }) {
    this.authCheckInProgress = false;

    // NOTE: The value here is the user UUID coming from user
    // migrated values into our new auth system from our legacy
    // auth system. This value may change over time in which
    // case this should be adjusted to look at the updated key.
    const userUUID = authResult.userData.auth_db_user_uuid;

    const user = {
      uuid: userUUID,
      username: authResult.userData.auth_db_user_username,
      email: authResult.userData.email,
      display_name: authResult.userData.name,
      first_name: authResult.userData.given_name,
      last_name: authResult.userData.family_name,
      is_active: true,
      is_staff: authResult.userData.role !== 'provider',
    };

    this.oidcSecurityService
      .getAccessToken()
      .pipe(
        take(1),
        switchMap((token) => {
          if (!token) {
            throw new Error('Missing access token');
          }

          // First dispatch with base user data
          this.store.dispatch(
            PLAuthSuccess({
              user: authResult.userData,
              token,
            })
          );

          // Proceed to fetch groups using authService
          return this.authService.getUser().pipe(
            map((result) => ({
              token,
              groups: result.groups || [],
            }))
          );
        })
      )
      .subscribe({
        next: ({ token, groups }) => {
          // Second dispatch after groups are fetched
          this.store.dispatch(
            PLAuthSuccess({
              user: { ...user, groups },
              token,
            })
          );
        },
        error: (error) => {
          this.store.dispatch(
            PLAuthError({
              error: error.message || 'Error during authentication.',
            })
          );
        },
      });

    this.accountsService
      .fetchAccounts()
      .pipe(
        mergeMap((accounts: any) => {
          const userAccount = accounts && accounts.length && accounts[0];
          const kangaUser$ = userAccount?.uuid
            ? this.accountsService.fetchAccountKangaUser(
                userAccount.uuid,
                userUUID
              )
            : of(null);
          const currentUser = authResult.userData;
          return kangaUser$.pipe(
            map((kangaUser: any) => ({ currentUser, accounts, kangaUser }))
          );
        })
      )
      .subscribe({
        next: ({ accounts, kangaUser }) => {
          this.heapLogger.setUser(authResult.userData);
          this.pendoLogger.setUser(authResult.userData, accounts, kangaUser);
        },
        error: (error) => {
          this.store.dispatch(PLAuthError({ error }));
        },
      });
  }
}
